/**
 * Date Formatter Utility
 *
 * Provides consistent date formatting across the application
 * Following the standard format: "Sep 17, 2025, 03:45 PM"
 */

/**
 * Parse a `datetime-local` input value (`YYYY-MM-DDTHH:mm`) into a local Date.
 */
export function parseDatetimeLocal(value: string): Date {
  const [datePart, timePart] = value.split("T");
  const [y, m, d] = (datePart || "").split("-").map(Number);
  const [h, min] = (timePart || "").split(":").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, h ?? 0, min ?? 0, 0);
}

/**
 * Format a Date object into the value expected by a `datetime-local` input
 * (`YYYY-MM-DDTHH:mm`), using the browser's local timezone.
 *
 * @param date - The Date to format
 * @returns Local datetime string in `YYYY-MM-DDTHH:mm` format
 */
export function formatDateTimeLocalValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Convert a `datetime-local` string (local browser time) to a UTC ISO 8601
 * string suitable for sending to the backend.
 *
 * Accepts both `YYYY-MM-DDTHH:mm` and `YYYY-MM-DDTHH:mm:ss` formats.
 *
 * DST note: `new Date(year, month, day, hours, minutes)` creates a date in the
 * browser's local timezone. During DST spring-forward transitions, times that
 * do not exist (e.g. 02:30 on the transition day) are normalised by the JS
 * engine to the next valid local time — this is standard JS behaviour and is
 * accepted here.
 *
 * @param localDateTime - Local datetime string from a `datetime-local` input
 * @returns UTC ISO string, or `null` if the input cannot be parsed
 */
export function localDateTimeToUtcIso(localDateTime: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(
    localDateTime,
  );
  if (!match) return null;

  const [, year, month, day, hours, minutes] = match;
  const localDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    0,
    0,
  );

  if (Number.isNaN(localDate.getTime())) return null;
  return localDate.toISOString();
}

/**
 * Format a date to the standard application format: "Sep 17, 2025, 03:45 PM"
 * @param dateInput - Date string, Date object, or null/undefined
 * @param fallback - Custom fallback text (default: "Unknown")
 * @returns Formatted date string or fallback text
 */
export function formatDateTime(
  dateInput: string | Date | null | undefined,
  fallback: string = "Unknown",
): string {
  if (!dateInput) return fallback;

  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "Invalid Date";
  }
}

/**
 * Date-only display: "Sep 17, 2025". Use for renewal / period-end columns
 * where the time-of-day is noise.
 */
export function formatDate(
  dateInput: string | Date | null | undefined,
  fallback: string = "Unknown",
): string {
  if (!dateInput) return fallback;

  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "Invalid Date";
  }
}

/**
 * Calendar year from an ISO date (`YYYY-MM-DD`) or month (`YYYY-MM`) key.
 * Uses the string prefix so local timezone cannot shift the year.
 */
export function isoDateYear(iso: string): string {
  return iso.slice(0, 4);
}

/**
 * True when the inclusive date range crosses a calendar year boundary.
 * Prefer filter/KPI bounds over sparse series points when both are available.
 */
export function dateRangeSpansMultipleYears(
  startIso: string | undefined | null,
  endIso: string | undefined | null,
): boolean {
  if (!startIso || !endIso) return false;
  return isoDateYear(startIso) !== isoDateYear(endIso);
}

/** `YYYY-MM-DD` of the viewer's local calendar date (not the UTC date). */
export function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Local calendar date `days` before (negative) or after a date, DST-safe. */
export function shiftLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * UTC instant for the start or end of a local calendar day, e.g.
 * `2026-10-07` in America/Halifax -> `2026-10-07T03:00:00Z` (start) and
 * `2026-10-08T02:59:59Z` (end). Sending the instant keeps the viewer's day
 * boundaries whatever the API does with offsets.
 */
export function localDayBoundaryUtcIso(
  isoDate: string,
  edge: "start" | "end",
): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const local =
    edge === "start"
      ? new Date(y!, m! - 1, d!, 0, 0, 0)
      : new Date(y!, m! - 1, d!, 23, 59, 59);
  return local.toISOString().replace(".000Z", "Z");
}

const ZONED_TIMESTAMP_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Local calendar date (`YYYY-MM-DD`) of an API timestamp. A timestamp with no
 * zone is UTC (the API serialises naive UTC datetimes). A bare date is kept.
 */
export function timestampToLocalIsoDate(timestamp: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) return timestamp;
  const date = new Date(
    ZONED_TIMESTAMP_RE.test(timestamp) ? timestamp : `${timestamp}Z`,
  );
  if (Number.isNaN(date.getTime())) return timestamp.slice(0, 10);
  return toLocalIsoDate(date);
}

/**
 * Chart label for a calendar bucket key: `YYYY-MM-DD` -> "Oct 7" (or
 * "Oct 7, 26" with the year) and `YYYY-MM` -> "Oct 2026". Formatted in UTC so
 * the viewer's timezone never moves the label to the previous day or month.
 */
export function formatBucketLabel(key: string, includeYear = false): string {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m) return key;
  if (key.length === 7) {
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  if (!d) return key;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
    ...(includeYear ? { year: "2-digit" as const } : {}),
  });
}

/**
 * Format a date for display in home greeting headers: "Wednesday, 27 May 2026".
 * Pass `timeZone` to use that zone's calendar date instead of the runtime's.
 */
export function formatLongDate(
  date: Date = new Date(),
  timeZone?: string,
): string {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(timeZone ? { timeZone } : {}),
  });
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    formatter.formatToParts(date).find((p) => p.type === type)?.value ?? "";
  return `${part("weekday")}, ${part("day")} ${part("month")} ${part("year")}`;
}

/**
 * Format a date into separate date and time components
 * Useful for displaying date and time on separate lines
 *
 * @param dateInput - Date string, Date object, or null/undefined
 * @returns Object with date and time strings
 *
 * @example
 * ```typescript
 * const { date, time } = formatDateTimeSplit("2025-12-19T10:30:00Z");
 * // date: "Dec 19, 2025"
 * // time: "10:30 AM"
 * ```
 */
export function formatDateTimeSplit(
  dateInput: string | Date | null | undefined,
): { date: string; time: string } {
  if (!dateInput) {
    return { date: "Unknown", time: "" };
  }

  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { date: "Invalid Date", time: "" };
    }

    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date: dateStr, time: timeStr };
  } catch (error) {
    console.warn("Date formatting error:", error);
    return { date: "Invalid Date", time: "" };
  }
}

const INVITATION_EXPIRY_DAYS = 7;

/**
 * Returns `true` when more than 7 days have elapsed since the later of
 * `createdAt` and `updatedAt` (falls back to `createdAt` when `updatedAt`
 * is absent).
 */
export function isInvitationExpired(
  createdAt: string,
  updatedAt?: string | null,
): boolean {
  const created = new Date(createdAt).getTime();
  const updated = updatedAt ? new Date(updatedAt).getTime() : 0;
  const referenceMs = Math.max(created, updated);
  return (
    Date.now() - referenceMs > INVITATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  );
}

/**
 * Format a date for relative time display (e.g., "2 hours ago", "3 days ago")
 * Falls back to standard date format for dates older than 7 days
 * @param dateInput - Date string, Date object, or null/undefined
 * @returns Formatted relative time string or standard date
 */
export function formatRelativeDate(
  dateInput: string | Date | null | undefined,
): string {
  if (!dateInput) return "Unknown";

  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // If older than 7 days, use standard date format
    if (diffInDays > 7) {
      return formatDateTime(date);
    }

    // Otherwise use relative time
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }

    return diffInDays === 1 ? "Yesterday" : `${diffInDays} days ago`;
  } catch (error) {
    console.warn("Date formatting error:", error);
    return "Invalid Date";
  }
}
